import { EQuizMode } from '@constants';
import { Modal } from 'antd';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ModalFooter } from './ModalFooter';
import { QuestionGenerationOptions } from './QuestionGenerationOptions';
import { Wrapper } from './QuizConfigModal.styles';
import { QuizConfigurationOptions } from './QuizConfigurationOptions';
import { QuizModeSelector } from './QuizModeSelector';

import { useQuizForm } from '@/hooks';
import { useGenerateQuiz } from '@/store';

const MODAL_WIDTH = 800;

interface Props {
  isFromBaseContent?: boolean;
}

export const QuizConfigModal: React.FC<Props> = ({ isFromBaseContent }) => {
  const { config, setConfig, isConfigOpen, setConfigOpen } = useQuizForm();
  const [currentStep, setCurrentStep] = useState(0);
  const { t } = useTranslation('ManagerQuizzesTranslations');
  const { isWorking: isGeneratingQuestions, generateQuiz } = useGenerateQuiz(
    config.mode,
    isFromBaseContent
  );

  useEffect(
    () => () => {
      setConfigOpen(false);
      setCurrentStep(0);
    },
    [setConfigOpen]
  );

  const handleSelectMode = (mode: EQuizMode) => {
    setConfig({ ...config, mode });
    handleNext();
  };

  const handleNext = () => {
    setCurrentStep(currentStep + 1);
  };

  const handleBack = () => {
    setCurrentStep(currentStep - 1);
    setConfig({ ...config, generationType: undefined });
  };

  const modalTitle = useMemo(() => {
    if (currentStep === 0) return t('SELECT_MODE');
    if (currentStep === 1) return t('MODE_OPTIONS', { mode: t(config.mode) });
    if (currentStep === 2) {
      if (config.generationType === 'AI') return t('AI_QUESTION_GENERATION');
      if (config.generationType === 'IMPORT')
        return t('IMPORT_QUESTIONS_FOR', { name: config.title });
    }
    return t('ADD_QUESTIONS', { name: config.title });
  }, [currentStep, config, t]);

  const handleGenerateQuiz = () => {
    if (isGeneratingQuestions || !config.section || !config.questionCount || !config.answersCount)
      return;
    generateQuiz(
      {
        sectionId: config.section,
        questionCount: config.questionCount!,
        mode: config.mode,
        answersCount: config.answersCount!
      },
      { onSuccess: () => setConfigOpen(false) }
    );
  };

  const slides = useMemo(
    () => [
      <QuizModeSelector onSelect={handleSelectMode} />,
      <QuizConfigurationOptions />,
      <QuestionGenerationOptions
        isGeneratingQuestions={isGeneratingQuestions}
        isFromBaseContent={isFromBaseContent}
      />
    ],
    [isGeneratingQuestions]
  );

  const handleClose = () => {
    setConfigOpen(false);
    setCurrentStep(0);
  };

  return (
    <Modal
      centered
      closable={!isGeneratingQuestions}
      footer={
        <ModalFooter
          step={currentStep}
          handleNextSlide={handleNext}
          handleBackSlide={handleBack}
          handleGenerateQuiz={handleGenerateQuiz}
          isLoading={isGeneratingQuestions}
          isFromBaseContent={isFromBaseContent}
        />
      }
      open={isConfigOpen}
      title={modalTitle}
      onCancel={handleClose}
      width={MODAL_WIDTH}>
      <Wrapper>{slides[currentStep]}</Wrapper>
    </Modal>
  );
};
